<template>
	<PageWrapper>
		<HeroesHome
			:hero="hero"
			:tracking-location="HOME_PAGE_LOCATIONS.hero"
			:defer-animations="!isPageReady"
		/>
		<LazyVectorsMobileFiber :defer-animations="!isPageReady" />
		<LazyVectorsAnimatedFiber :defer-animations="!isPageReady" />
		<SectionContentContainer classes="-mt-16 pb-12 lg:pb-16 min-h-[500px] contain-layout">
			<template #items>
				<LazyVisibleSectionContentItem
					v-for="(item, index) in $tm('home.section-items')"
					:key="item"
					:copy="item"
					:icon="HOME_SECTION_ITEMS[index]?.icon"
					:viewbox="HOME_SECTION_ITEMS[index]?.viewbox"
					:image="HOME_SECTION_ITEMS[index]?.image"
					:lines="HOME_SECTION_ITEMS[index]?.lines"
					:circles="HOME_SECTION_ITEMS[index]?.circles"
					hydrate-on-visible
					:lazy="index > 2"
					:defer-animations="!isPageReady"
					class="min-h-[200px] contain-layout -mt-8 md:-mt-0"
				/>
			</template>
		</SectionContentContainer>

		<div class="contain-layout">
			<LazyVisibleTestimonials
				hydrate-on-visible
				:tracking-location="HOME_PAGE_LOCATIONS.testimonials"
				class="contain-layout"
			/>
			<LazyVisibleServiceAreasCarousel
				hydrate-on-visible
				class="contain-layout"
			/>
			<LazyVisibleFaqs
				hydrate-on-visible
				:tracking-location="HOME_PAGE_LOCATIONS.faq"
				:faqs="faqs"
				divider="both"
				class="contain-layout"
			/>
			<LazyVisibleBlogCarousel
				id="home-page"
				:tracking-location="HOME_PAGE_LOCATIONS.blogCarousel"
				hydrate-on-visible
				class="contain-layout"
			/>
			<LazyVisibleBannersAddressChecker
				:tracking-location="HOME_PAGE_LOCATIONS.ctaBanner"
				hydrate-on-visible
				class="contain-layout"
			/>
		</div>
	</PageWrapper>
</template>

<script setup lang="ts">
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'

const [HOME_SECTION_ITEMS, HOME_PAGE_LOCATIONS] = await Promise.all([
	import('@/constants/components/home-sections').then(m => m.HOME_SECTION_ITEMS),
	import('@/constants/ga4/tracking-ids/pages/home').then(m => m.HOME_PAGE_LOCATIONS)
])

const constants = await import('@/constants/index.js')
const jsonLdConstants = await import('@/constants/json-ld')
const ogImageConstants = await import('@/constants/og-image')


const {
	BASE_URL,
	COMPANY_NAME,
	COMPANY_PHONE
} = constants

const {
	JSON_LD_AGGREGATE_RATING,
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_LATITUDE,
	JSON_LD_LOGO,
	JSON_LD_LONGITUDE,
	JSON_LD_ORGANIZATION_ADDRESS,
	JSON_LD_ORGANIZATION_AREA_SERVED,
	JSON_LD_ORGANIZATION_CONTACT_POINT,
	JSON_LD_ORGANIZATION_REVIEWS,
	JSON_LD_ORGANIZATION_SAME_AS,
	JSON_LD_PRICE_RANGE
} = jsonLdConstants

const { OG_IMAGE_DEFAULT } = ogImageConstants
const { mapFaqsForJsonLd } = await import('@/utils/mapFaqsForJsonLd')
const { useI18n } = await import('vue-i18n')

const { t } = useI18n({
	useScope: 'global'
})

// Animation state management
const isPageReady = ref(false)

// Load Storyblok data without blocking
const { getStory } = useStoryblokWithErrorReporting()
const storyblokData = await getStory('home')
const { story } = storyblokData.value?.data || {}

const hero = computed(() => {
	return story?.content?.hero?.[0]
})

const faqs = computed(() => {
	return story?.content?.faqContent
})

const jsonLdFaqs = computed(() => {
	// Only compute when needed and page is ready
	if (!isPageReady.value || !faqs.value) return []
	return mapFaqsForJsonLd(faqs.value)
})

// Enable animations after LCP image loads
onMounted(() => {
	// Wait for the LCP image to load first
	const lcpImage = document.querySelector('img[fetchpriority="high"]') as HTMLImageElement

	if (lcpImage) {
		if (lcpImage.complete) {
			// Image already loaded
			enableAnimationsAfterDelay()
		} else {
			// Wait for the image to load
			lcpImage.addEventListener('load', enableAnimationsAfterDelay, { once: true })
			// Fallback timeout
			setTimeout(enableAnimationsAfterDelay, 1000)
		}
	} else {
		// No LCP image found, enable animations after a short delay
		enableAnimationsAfterDelay()
	}
})

function enableAnimationsAfterDelay() {
	// Use requestIdleCallback for better performance
	if ('requestIdleCallback' in window) {
		requestIdleCallback(() => {
			isPageReady.value = true
		}, { timeout: 200 })
	} else {
		setTimeout(() => {
			isPageReady.value = true
		}, 50)
	}
}

// Pre-compute static translation values
const metaTitle = t('meta-title')
const metaDescription = t('meta-description', { COMPANY_NAME })


useHead({
	title: metaTitle,
	meta: [
		{ name: 'description',
			content: metaDescription },
		{ name: 'url',
			content: BASE_URL }
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})

const {
	title = '',
	description = ''
} = story?.content?.metatags || {}

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/og-image-fallback.png',
	twitterCard: () => 'summary_large_image'
})

 import.meta.prerender && defineOgImage('Default', OG_IMAGE_DEFAULT)

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'LocalBusiness',
			'name': COMPANY_NAME,
			'url': BASE_URL,
			'logo': JSON_LD_LOGO,
			'image': JSON_LD_LOGO,
			'telephone': COMPANY_PHONE,
			'description': metaDescription,
			'contactPoint': JSON_LD_ORGANIZATION_CONTACT_POINT,
			'sameAs': JSON_LD_ORGANIZATION_SAME_AS,
			'address': JSON_LD_ORGANIZATION_ADDRESS,
			'areaServed': JSON_LD_ORGANIZATION_AREA_SERVED,
			'hasOfferCatalog': JSON_LD_HAS_OFFER_CATALOG,
			'review': JSON_LD_ORGANIZATION_REVIEWS,
			'aggregateRating': JSON_LD_AGGREGATE_RATING,
			'priceRange': JSON_LD_PRICE_RANGE,
			'geo': {
				'@type': 'GeoCoordinates',
				'latitude': JSON_LD_LATITUDE,
				'longitude': JSON_LD_LONGITUDE
			}
		},
		{
			'@type': 'Service',
			'name': COMPANY_NAME,
			'logo': JSON_LD_LOGO,
			'provider': {
				'@type': 'Organization',
				'name': COMPANY_NAME,
				'telephone': COMPANY_PHONE
			},
			'serviceType': 'Fiber-Optic Internet Access',
			'serviceOutput': 'Internet service',
			'areaServed': JSON_LD_ORGANIZATION_AREA_SERVED,
			'hasOfferCatalog': JSON_LD_HAS_OFFER_CATALOG
		},
		{
			'@type': 'FAQPage',
			'mainEntity': jsonLdFaqs.value
		}
	]
})
</script>
